import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Folder, ArrowLeft, Loader, AlertCircle, Plus, X, Edit2, Check, Trash2 } from 'lucide-react';
import { API } from '../App';
import Header from '../components/layout/Header';
import LauncherCard from '../components/projects/LauncherCard';
import FrayList from '../components/projects/FrayList';
import FilesTree from '../components/projects/FilesTree';
import ProjectBriefPanel from '../components/projects/ProjectBriefPanel';
import AddVersionInline from '../components/projects/AddVersionInline';
import { uploadProjectFile } from '../lib/uploadProjectFile';

/**
 * Project detail page (#1101) — two-column shell mirroring Claude Desktop.
 * Left: LauncherCard + FrayList. Right: Project Brief + Files (placeholders
 * filled in by Phase 2 and Phase 3 of the plan).
 */
export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [frayContextsById, setFrayContextsById] = useState({});
  const [briefContextWithContent, setBriefContextWithContent] = useState(null);
  const [showAttachFray, setShowAttachFray] = useState(false);
  const [pickerCandidates, setPickerCandidates] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [showRenameProject, setShowRenameProject] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // #1221: per-file upload state. Keyed by scope (project / fray-<id>);
  // each entry is a list of {filename, status, error} so the UI can
  // surface progress + failures per file. Pre-fix this was a single
  // counter and errors were swallowed to console.error — users saw
  // "uploading N" decrement to zero whether everything succeeded or
  // every file silently failed.
  const [uploadsByScope, setUploadsByScope] = useState({});
  // Backwards-compat counter the FilesTree component reads. Derived
  // from uploadsByScope length where status === 'uploading'.
  const uploadingByScope = Object.fromEntries(
    Object.entries(uploadsByScope).map(([k, list]) => [
      k, list.filter((u) => u.status === 'uploading').length,
    ])
  );

  const scopeKey = (scope) =>
    scope.type === 'project' ? 'project' : `fray-${scope.frayId}`;

  const onAddFile = async (scope, fileList) => {
    const key = scopeKey(scope);
    const files = Array.from(fileList);

    // Seed an entry per file so the UI shows "uploading <name>" rows
    // immediately. Each entry transitions through 'uploading' → 'done'
    // or 'failed' as the per-file work completes.
    setUploadsByScope((s) => ({
      ...s,
      [key]: [
        ...(s[key] || []).filter((u) => u.status === 'uploading'),
        ...files.map((f) => ({
          filename: f.name,
          status: 'uploading',
          error: null,
          startedAt: Date.now(),
        })),
      ],
    }));

    // #1230: fire all files concurrently. Pre-fix this was a serial
    // for-await loop that made 4 small files take 4× the time of one.
    // Each file is independent (separate blob, separate job, separate
    // contexts row + project_contexts insert) so parallelism is safe.
    // Promise.all over .map fans out the upload+poll+attach pipelines
    // simultaneously; the order entries appear in the project doesn't
    // matter (sorted by filename / created_at on render).
    await Promise.all(files.map(async (file) => {
      try {
        await uploadProjectFile(file, scope);
        // Mark this file done and refresh the project view so the
        // file appears in the folder progressively (#1221: was
        // batched at end-of-loop only).
        setUploadsByScope((s) => ({
          ...s,
          [key]: (s[key] || []).map((u) =>
            u.filename === file.name && u.status === 'uploading'
              ? { ...u, status: 'done' }
              : u
          ),
        }));
        fetchProject();
      } catch (e) {
        // #1221: surface the failure in the UI instead of swallowing.
        const msg = e?.response?.data?.detail || e?.message || String(e);
        // eslint-disable-next-line no-console
        console.error('Upload failed:', file.name, e);
        setUploadsByScope((s) => ({
          ...s,
          [key]: (s[key] || []).map((u) =>
            u.filename === file.name && u.status === 'uploading'
              ? { ...u, status: 'failed', error: msg }
              : u
          ),
        }));
      }
    }));

    // Auto-prune 'done' entries after 4s so the upload list doesn't
    // accumulate; 'failed' entries stay until the user dismisses
    // (caller can clear via clearUploadStatus).
    setTimeout(() => {
      setUploadsByScope((s) => ({
        ...s,
        [key]: (s[key] || []).filter((u) => u.status !== 'done'),
      }));
    }, 4000);
  };

  const clearUploadStatus = (scope, filename) => {
    const key = scopeKey(scope);
    setUploadsByScope((s) => ({
      ...s,
      [key]: (s[key] || []).filter((u) => u.filename !== filename),
    }));
  };

  const deleteProject = async () => {
    setDeleting(true);
    try {
      const r = await API.delete(`/api/projects/${projectId}`);
      const detached = r?.data?.frays_detached;
      navigate('/projects', {
        state: {
          flash: detached
            ? `Project deleted. ${detached} fray${detached === 1 ? '' : 's'} detached (not deleted).`
            : 'Project deleted.',
        },
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to delete project');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const openRenameProject = () => {
    setDraftName(project?.name || '');
    setDraftDescription(project?.description || '');
    setShowRenameProject(true);
  };

  const saveProjectMeta = async () => {
    if (!draftName.trim()) return;
    setSavingMeta(true);
    try {
      await API.patch(`/api/projects/${projectId}`, {
        name: draftName.trim(),
        description: draftDescription.trim() || null,
      });
      setShowRenameProject(false);
      fetchProject();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to update project');
    }
    setSavingMeta(false);
  };


  const openAttachFray = async () => {
    setShowAttachFray(true);
    setPickerLoading(true);
    try {
      const r = await API.get('/api/frays');
      const all = r.data?.frays || [];
      const attachedIds = new Set((project?.frays || []).map((f) => f.id));
      setPickerCandidates(all.filter((f) => !attachedIds.has(f.id)));
    } catch {
      setPickerCandidates([]);
    }
    setPickerLoading(false);
  };

  const attachFray = async (frayId) => {
    try {
      await API.post(`/api/projects/${projectId}/frays/${frayId}`);
      setShowAttachFray(false);
      fetchProject();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to attach fray');
    }
  };

  const removeFray = async (frayId) => {
    if (!window.confirm('Remove this fray from the project? The fray itself is preserved.')) return;
    try {
      await API.delete(`/api/projects/${projectId}/frays/${frayId}`);
      fetchProject();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to remove fray');
    }
  };

  const fetchProject = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await API.get(`/api/projects/${projectId}`);
      setProject(r.data);
      const projectContexts = r.data?.contexts || [];
      const briefMeta = projectContexts.find((c) => c.role === 'instructions');
      if (briefMeta?.context_id) {
        try {
          const cr = await API.get(`/api/contexts/${briefMeta.context_id}`);
          setBriefContextWithContent({
            ...briefMeta,
            content: cr.data?.content || '',
          });
        } catch {
          setBriefContextWithContent(briefMeta);
        }
      } else {
        setBriefContextWithContent(null);
      }
      const frays = r.data?.frays || [];
      if (frays.length > 0) {
        const results = await Promise.all(
          frays.map((f) =>
            API.get(`/api/frays/${f.id}/contexts`)
              .then((res) => [f.id, res.data?.direct || []])
              .catch(() => [f.id, []]),
          ),
        );
        const map = {};
        results.forEach(([id, ctxs]) => { map[id] = ctxs; });
        setFrayContextsById(map);
      } else {
        setFrayContextsById({});
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load project');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F1E8]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-10 text-center text-[#A0A0A0] font-mono text-sm flex items-center justify-center gap-2">
          <Loader className="w-4 h-4 animate-spin" /> Loading project…
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F1E8]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-10">
          <div className="p-4 bg-[#A61E17]/20 border border-[#A61E17]/40 rounded-lg flex items-center gap-3 text-sm">
            <AlertCircle className="w-5 h-5 text-[#A61E17]" />
            <span className="text-[#F5F1E8]">{error || 'Project not found'}</span>
          </div>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 mt-6 text-xs font-mono text-[#A0A0A0] hover:text-[#F5F1E8]"
          >
            <ArrowLeft className="w-3 h-3" /> Back to projects
          </Link>
        </div>
      </div>
    );
  }

  const frays = project.frays || [];
  const enrichedProject = {
    ...project,
    frays: frays.map((f) => ({
      ...f,
      fray_contexts: frayContextsById[f.id] || [],
    })),
  };
  const briefContexts = briefContextWithContent ? [briefContextWithContent] : [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F1E8]" data-testid="project-detail-page">
      <Header />
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-10">
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 mb-6 text-xs font-mono text-[#A0A0A0] hover:text-[#F5F1E8]"
        >
          <ArrowLeft className="w-3 h-3" /> Projects
        </Link>

        <div className="mb-8 flex items-start gap-3">
          <Folder className="w-7 h-7 text-[#F2A900] shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#F5F1E8]">
                {project.name}
              </h1>
              <button
                type="button"
                onClick={openRenameProject}
                data-testid="project-rename-btn"
                className="p-2 text-[#A0A0A0] hover:text-[#F5F1E8]"
                title="Edit project name + description"
                aria-label="Edit project name and description"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="project-delete-btn"
                className="p-2 text-[#A0A0A0] hover:text-[#A61E17]"
                title="Delete project"
                aria-label="Delete project"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {project.description && (
              <p className="text-sm text-[#A0A0A0] mt-1 max-w-3xl">
                {project.description}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <div data-testid="project-launcher">
              <LauncherCard projectId={projectId} frays={frays} />
            </div>
            <div data-testid="project-fray-list">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-mono uppercase tracking-wider text-[#A0A0A0]">
                  Frays in this project
                </h2>
                <button
                  type="button"
                  onClick={openAttachFray}
                  data-testid="attach-fray-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E1E1E] border border-[#2A2A2A] hover:border-[#FF4D00] text-[10px] font-mono uppercase tracking-wider text-[#F5F1E8] rounded transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Attach existing
                </button>
              </div>
              <FrayList frays={frays} onRemove={removeFray} />
            </div>
          </div>
          <div className="lg:col-span-2 space-y-6">
            <ProjectBriefPanel
              projectId={projectId}
              contexts={briefContexts}
              onSaved={fetchProject}
            />
            <div
              data-testid="project-files-slot"
              className="p-5 bg-[#1E1E1E] border border-[#2A2A2A] rounded-lg"
            >
              <FilesTree
                project={enrichedProject}
                projectId={projectId}
                onPromoted={fetchProject}
                onDetach={fetchProject}
                onAddFile={onAddFile}
                uploadingByScope={uploadingByScope}
                uploadsByScope={uploadsByScope}
                clearUploadStatus={clearUploadStatus}
              />
              <AddVersionInline projectId={projectId} onAdded={fetchProject} />
            </div>
          </div>
        </div>

        {showRenameProject && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
            data-testid="project-rename-modal"
          >
            <div className="w-full max-w-xl bg-[#1E1E1E] border border-[#2A2A2A] rounded-lg p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#F5F1E8]">
                  Edit Project
                </h3>
                <button
                  type="button"
                  onClick={() => setShowRenameProject(false)}
                  className="p-1 text-[#A0A0A0] hover:text-[#F5F1E8]"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <label className="block text-[10px] font-mono text-[#A0A0A0] uppercase tracking-wider">
                Name
              </label>
              <input
                type="text"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                data-testid="project-rename-name-input"
                autoFocus
                className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-sm text-[#F5F1E8] focus:border-[#FF4D00] focus:outline-none"
              />
              <label className="block text-[10px] font-mono text-[#A0A0A0] uppercase tracking-wider">
                Description (optional)
              </label>
              <textarea
                value={draftDescription}
                onChange={(e) => setDraftDescription(e.target.value)}
                data-testid="project-rename-description-input"
                rows={3}
                className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-sm text-[#F5F1E8] focus:border-[#FF4D00] focus:outline-none"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRenameProject(false)}
                  className="px-3 py-1.5 text-xs font-mono text-[#A0A0A0] hover:text-[#F5F1E8]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveProjectMeta}
                  disabled={savingMeta || !draftName.trim()}
                  data-testid="project-rename-save"
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-[#FF4D00] hover:bg-[#FF6A2A] disabled:bg-[#2A2A2A] disabled:text-[#A0A0A0] text-[#0A0A0A] font-mono text-xs uppercase rounded transition-colors"
                >
                  <Check className="w-3 h-3" />
                  {savingMeta ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
            data-testid="project-delete-modal"
          >
            <div className="w-full max-w-md bg-[#1E1E1E] border border-[#A61E17]/40 rounded-lg p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#A61E17] flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  Delete project?
                </h3>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="p-1 text-[#A0A0A0] hover:text-[#F5F1E8]"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs font-mono text-[#F5F1E8]">
                This permanently deletes <span className="text-[#FF4D00]">{project.name}</span>{' '}
                and all attached project contexts.
              </p>
              <p className="text-[10px] font-mono text-[#A0A0A0]">
                Frays inside the project are <span className="text-[#F5F1E8]">detached, not deleted</span>{' '}
                — they keep their history and become standalone frays.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-3 py-1.5 text-xs font-mono text-[#A0A0A0] hover:text-[#F5F1E8] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={deleteProject}
                  disabled={deleting}
                  data-testid="project-delete-confirm"
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-[#A61E17] hover:bg-[#C92D24] disabled:bg-[#2A2A2A] disabled:text-[#A0A0A0] text-[#F5F1E8] font-mono text-xs uppercase rounded transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAttachFray && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
            data-testid="attach-fray-modal"
          >
            <div className="w-full max-w-xl bg-[#1E1E1E] border border-[#2A2A2A] rounded-lg p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#F5F1E8]">
                  Attach existing fray
                </h3>
                <button
                  type="button"
                  onClick={() => setShowAttachFray(false)}
                  className="p-1 text-[#A0A0A0] hover:text-[#F5F1E8]"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {pickerLoading ? (
                <div className="text-center py-6 text-[#A0A0A0] font-mono text-xs flex items-center justify-center gap-2">
                  <Loader className="w-3 h-3 animate-spin" /> Loading…
                </div>
              ) : pickerCandidates.length === 0 ? (
                <div className="text-center py-6 text-[#A0A0A0] font-mono text-xs">
                  No unattached frays. Run a fray first or attach via{' '}
                  <code className="text-[#00D4FF]">fray_attach_fray_to_project</code> MCP tool.
                </div>
              ) : (
                <ul className="space-y-1 max-h-72 overflow-y-auto">
                  {pickerCandidates.map((f) => (
                    <li
                      key={f.id}
                      onClick={() => attachFray(f.id)}
                      data-testid={`attach-candidate-${f.id}`}
                      className="flex items-center gap-3 p-2 bg-[#0F0F0F] border border-[#2A2A2A] hover:border-[#FF4D00] rounded text-xs font-mono cursor-pointer"
                    >
                      <span className="flex-1 text-[#F5F1E8] truncate">
                        {f.title || f.template_name || f.id.slice(0, 8)}
                      </span>
                      <span className="text-[#A0A0A0] text-[10px]">{f.status}</span>
                      <Plus className="w-3 h-3 text-[#FF4D00]" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
